import React, { useEffect, useState } from 'react';
import { getPaymentTerms, startCheckout, verifyPayment } from '../Api/Subscriptionapi';
import { loadRazorpay, openCheckout } from '../Api/razorpayCheckout';
import '../Style/PaymentModal.css';

const money = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// The server decides if the payment is real. If Razorpay is still
// confirming, ask again a few times.
async function confirm(response) {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const data = await verifyPayment({
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_subscription_id: response.razorpay_subscription_id,
      razorpay_signature: response.razorpay_signature,
    });
    if (!data.pending) return data.subscription;
    await wait(2000);
  }
  throw new Error('We are still confirming your payment. Please check your dashboard in a minute.');
}

export default function PaymentModal({ planKey, onClose, onSuccess }) {
  const [terms, setTerms] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [phase, setPhase] = useState('loading'); // loading | ready | paying | confirming | error
  const [error, setError] = useState('');

  const busy = phase === 'paying' || phase === 'confirming';

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const t = await getPaymentTerms(planKey);
        if (off) return;
        setTerms(t);
        setPhase('ready');
      } catch (err) {
        if (off) return;
        setError(err.message);
        setPhase('error');
      }
    })();
    return () => {
      off = true;
    };
  }, [planKey]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [busy, onClose]);

  async function pay() {
    setError('');
    setPhase('paying');
    try {
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error('Could not load the payment window. Check your connection and try again.');

      const session = await startCheckout(planKey);
      openCheckout({
        key: session.key,
        subscriptionId: session.subscription_id,
        description: session.switching
          ? `Switch to ${session.plan.label} plan`
          : session.trial
          ? `${session.plan.label} plan · ${terms.trial_days}-day free trial`
          : `${session.plan.label} plan`,
        prefill: session.prefill,
        onSuccess: async (response) => {
          setPhase('confirming');
          try {
            const sub = await confirm(response);
            onSuccess(sub);
          } catch (err) {
            setError(err.message);
            setPhase('error');
          }
        },
        onDismiss: () => setPhase((p) => (p === 'paying' ? 'ready' : p)),
        onFailed: (message) => {
          setError(message || 'The payment failed. Your plan price was not charged. Please try again.');
          setPhase('error');
        },
      });
    } catch (err) {
      setError(err.message);
      setPhase('error');
    }
  }

  const switching = !!(terms && terms.switching);

  return (
    <div className="paym__overlay" onClick={() => !busy && onClose()}>
      <div className="paym" role="dialog" aria-modal="true" aria-label="Payment terms" onClick={(e) => e.stopPropagation()}>
        {!busy && (
          <button type="button" className="paym__x" aria-label="Close" onClick={onClose}>
            &times;
          </button>
        )}

        {phase === 'loading' && <p className="paym__msg">Loading payment details…</p>}

        {phase === 'confirming' && (
          <div className="paym__confirming">
            <span className="paym__spinner" aria-hidden="true" />
            <p className="paym__msg">Confirming your payment…</p>
            <p className="paym__small">Please don&rsquo;t close this window.</p>
          </div>
        )}

        {terms && phase !== 'loading' && phase !== 'confirming' && (
          <>
            <p className="paym__eyebrow">{terms.label} plan</p>
            <h2 className="paym__title">
              {switching
                ? `Switch to the ${terms.label} plan`
                : terms.trial
                ? `Start your ${terms.trial_days}-day free trial`
                : 'Subscribe to MLink'}
            </h2>

            {switching ? (
              <dl className="paym__list">
                <div>
                  <dt>Pay today</dt>
                  <dd>
                    No plan charge
                    <span>Razorpay may place a small AutoPay verification charge on your payment method</span>
                  </dd>
                </div>
                <div>
                  <dt>Your plan</dt>
                  <dd>
                    {terms.current_label} &rarr; {terms.label}
                  </dd>
                </div>
                <div>
                  <dt>Free trial</dt>
                  <dd>
                    Continues <span>until {formatDateTime(terms.first_debit_at)}</span>
                  </dd>
                </div>
                <div>
                  <dt>After the trial</dt>
                  <dd>
                    {money(terms.price)} <span>{terms.billing}</span>
                  </dd>
                </div>
                <div>
                  <dt>First debit</dt>
                  <dd>{formatDateTime(terms.first_debit_at)}</dd>
                </div>
                <div>
                  <dt>Renewal</dt>
                  <dd>
                    Automatic <span>until you cancel</span>
                  </dd>
                </div>
              </dl>
            ) : (
              <dl className="paym__list">
                <div>
                  <dt>{terms.trial ? 'Pay today' : 'Pay now'}</dt>
                  <dd>
                    {money(terms.upfront_amount)}
                    {terms.trial && <span> trial activation fee</span>}
                  </dd>
                </div>
                {terms.trial && (
                  <div>
                    <dt>Free trial</dt>
                    <dd>
                      {terms.trial_days} days <span>until {formatDateTime(terms.first_debit_at)}</span>
                    </dd>
                  </div>
                )}
                {terms.trial && (
                  <div>
                    <dt>During the trial</dt>
                    <dd>
                      All features <span>1 link only</span>
                    </dd>
                  </div>
                )}
                <div>
                  <dt>{terms.trial ? 'After the trial' : 'Then'}</dt>
                  <dd>
                    {money(terms.price)} <span>{terms.billing}</span>
                  </dd>
                </div>
                {terms.trial && (
                  <div>
                    <dt>First debit</dt>
                    <dd>{formatDateTime(terms.first_debit_at)}</dd>
                  </div>
                )}
                <div>
                  <dt>Renewal</dt>
                  <dd>
                    Automatic <span>until you cancel</span>
                  </dd>
                </div>
              </dl>
            )}

            <p className="paym__small">
              {switching
                ? `Your ${terms.current_label} plan is cancelled as soon as ${terms.label} is authorized, so you are never charged for both. The ₹1 you already paid is not charged again. Cancel any time from Dashboard → Manage subscription.`
                : `Cancel any time from Dashboard → Manage subscription.${
                    terms.trial
                      ? ` Cancel before ${formatDateTime(terms.first_debit_at)} and you won't be charged ${money(terms.price)}. The ${money(terms.upfront_amount)} trial fee is not refundable.`
                      : ''
                  }`}
            </p>

            <label className="paym__consent">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <span>
                I authorize MLink to charge {money(terms.price)} {terms.billing} to my selected payment method through
                Razorpay AutoPay{terms.trial ? `, starting ${formatDateTime(terms.first_debit_at)}` : ''}, until I
                cancel.
              </span>
            </label>

            {error && <p className="paym__error">{error}</p>}

            <button
              type="button"
              className="paym__btn"
              disabled={!agreed || phase === 'paying'}
              onClick={pay}
            >
              {phase === 'paying'
                ? 'Opening payment…'
                : phase === 'error'
                ? 'Try again'
                : switching
                ? `Authorize & switch to ${terms.label}`
                : terms.trial
                ? `Pay ${money(terms.upfront_amount)} & start free trial`
                : `Pay ${money(terms.upfront_amount)} & subscribe`}
            </button>
          </>
        )}

        {!terms && phase === 'error' && (
          <>
            <p className="paym__error">{error}</p>
            <button type="button" className="paym__btn paym__btn--ghost" onClick={onClose}>
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}